/**
 * Initialise les événements des boutons des menus d'accès rapide.
 * @param {(HTMLButtonElement|HTMLAnchorElement)[]} quickAccessMenus les éléments du menu.
 */
function initEventsButton(quickAccessMenus) {
    for (const currentMenu of quickAccessMenus) {
        currentMenu.addEventListener('keydown', handleKeyDownMenu(quickAccessMenus), true);
        if (currentMenu.popoverTargetElement) {
            const subMenu = currentMenu.popoverTargetElement;
            const subMenuItems = Array.from(subMenu.querySelectorAll('.js-quick-access__link'));
            subMenu.addEventListener('keydown', handleKeyDownItems(subMenuItems, quickAccessMenus), true);
        }
    }
}

/**
 * Fabrique de gestionnaire d'événement clavier pour les menus.
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 * @return {EventListener} le gestionnaire
 */
function handleKeyDownMenu(menuButtons) {

    /**
     * Gestionnaire d'événement clavier pour les menus
     */
    return function handleKeyDownEventHandler(event) {
        const currentButton = event.currentTarget;
        const currentButtonIndex = menuButtons.findIndex((b) => b === currentButton);

        const keysAction = {
            ArrowDown: focusFirstItemSubMenu,
            ArrowLeft: openPreviousMenu,
            ArrowRight: openNextMenu,
            Home: openFirstMenu,
            End: openLastMenu
        };

        if (Object.hasOwn(keysAction, event.key)) {
            keysAction[event.key]({currentButtonIndex, menuButtons});
            event.stopPropagation();
            event.preventDefault();
        }
    };
}


/**
 * Ouverture du menu précédent.
 * @param {Number} currentButtonIndex index du menu courant
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 */
function openPreviousMenu({currentButtonIndex, menuButtons}) {
    if (currentButtonIndex > 0) {
        menuButtons[currentButtonIndex].popoverTargetElement?.hidePopover();
        const menuEntry = menuButtons[currentButtonIndex - 1];
        menuEntry.focus();
        if (menuEntry.popoverTargetElement) {
            menuEntry.popoverTargetElement.showPopover();
            focusFirstItemSubMenu({currentButtonIndex: currentButtonIndex - 1, menuButtons});
        }
    }
}

/**
 * Ouverture du menu suivant.
 * @param {Number} currentButtonIndex index du menu courant
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 */
function openNextMenu({currentButtonIndex, menuButtons}) {
    if (currentButtonIndex < menuButtons.length - 1) {
        menuButtons[currentButtonIndex].popoverTargetElement?.hidePopover();
        const menuEntry = menuButtons[currentButtonIndex + 1];
        menuEntry.focus();
        if (menuEntry.popoverTargetElement) {
            menuEntry.popoverTargetElement.showPopover();
            focusFirstItemSubMenu({currentButtonIndex: currentButtonIndex + 1, menuButtons});
        }
    }
}

/**
 * Ouverture du premier menu.
 * @param {Number} currentButtonIndex index du menu courant
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 */
function openFirstMenu({currentButtonIndex, menuButtons}) {
    if (currentButtonIndex !== 0) {
        menuButtons[currentButtonIndex].popoverTargetElement?.hidePopover();
        const menuEntry = menuButtons[0];
        menuEntry.focus();
        if (menuEntry.popoverTargetElement) {
            menuEntry.popoverTargetElement.showPopover();
            focusFirstItemSubMenu({currentButtonIndex: 0, menuButtons});
        }
    }
}

/**
 * Ouverture du dernier menu.
 * @param {Number} currentButtonIndex index du menu courant
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 */
function openLastMenu({currentButtonIndex, menuButtons}) {
    if (currentButtonIndex !== menuButtons.length - 1) {
        menuButtons[currentButtonIndex].popoverTargetElement?.hidePopover();
        const menuEntry = menuButtons[menuButtons.length - 1];
        menuEntry.focus();
        if (menuEntry.popoverTargetElement) {
            menuEntry.popoverTargetElement.showPopover();
            focusFirstItemSubMenu({currentButtonIndex: menuButtons.length - 1, menuButtons});
        }
    }
}

/**
 * Place le focus sur le premier item du menu.
 * @param {Number} currentButtonIndex index du menu courant
 * @param {HTMLButtonElement|HTMLAnchorElement[]} menuButtons boutons du menu
 */
function focusFirstItemSubMenu({currentButtonIndex, menuButtons}) {
    const menuEntry = menuButtons[currentButtonIndex].popoverTargetElement;
    menuEntry.showPopover();
    if (menuEntry) {
        menuEntry.querySelector('.js-quick-access__link').focus();
    }
}

/**
 * Fabrique de gestionnaire d'événement clavier pour les sous-menus.
 * @param {HTMLElement[]} subMenuItems items du menu
 * @param {HTMLButtonElement|HTMLAnchorElement[]} parentMenus boutons du menu parent
 * @return {EventListener} le gestionnaire
 */
function handleKeyDownItems(subMenuItems, parentMenus) {

    /**
     * Gestionnaire d'événement clavier pour les sous-menus.
     */
    return function handleKeyDownItemsEventHandler(event) {
        const currentItem = event.target;
        const currentItemIndex = subMenuItems.findIndex((b) => b === currentItem);

        const keysAction = {
            ArrowLeft: openPreviousParentMenu,
            ArrowRight: openNextParentMenu,
            ArrowUp: focusPreviousItemMenu,
            ArrowDown: focusNextItemMenu,
            Home: focusFirstItemMenu,
            End: focusLastItemMenu
        };

        if (Object.hasOwn(keysAction, event.key)) {
            keysAction[event.key]({currentItemIndex, subMenuItems, currentItem, parentMenus});
            event.stopPropagation();
            event.preventDefault();
        }
    };
}

/**
 * Place le focus sur l'item précédent dans le menu.
 * @param {Number} currentItemIndex index de l'item courant
 * @param {HTMLElement[]} subMenuItems Item du sous menu
 */
function focusPreviousItemMenu({currentItemIndex, subMenuItems}) {
    if (currentItemIndex > 0) {
        const item = subMenuItems[currentItemIndex - 1];
        item.focus();
    }
}

/**
 * Place le focus sur l'item suivant dans le menu.
 * @param {Number} currentItemIndex index de l'item courant
 * @param {HTMLElement[]} subMenuItems Item du sous menu
 */
function focusNextItemMenu({currentItemIndex, subMenuItems}) {
    if (currentItemIndex < subMenuItems.length - 1) {
        const item = subMenuItems[currentItemIndex + 1];
        item.focus();
    }
}

/**
 * Place le focus sur le premier item dans le menu.
 * @param {HTMLElement[]} subMenuItems Item du sous menu
 */
function focusFirstItemMenu({subMenuItems}) {
    subMenuItems[0]?.focus();
}

/**
 * Place le focus sur le dernier item dans le menu.
 * @param {HTMLElement[]} subMenuItems Item du sous menu
 */
function focusLastItemMenu({subMenuItems}) {
    subMenuItems[subMenuItems.length - 1]?.focus();
}

/**
 * Ouvre le menu parent précédent.
 * @param {HTMLElement} currentItem item sélectionné dans le sous menu
 * @param {HTMLButtonElement|HTMLAnchorElement[]} parentMenus menus parents
 */
function openPreviousParentMenu({currentItem, parentMenus}) {
    const currentParentMenuButton = currentItem.closest('.js-quick-access__item')?.querySelector('.js-quick-access__entry');
    const currentParentMenuButtonIndex = parentMenus.findIndex((b) => b === currentParentMenuButton);
    openPreviousMenu({currentButtonIndex: currentParentMenuButtonIndex, menuButtons: parentMenus});
}

/**
 * Ouvre le menu parent suivant.
 * @param {HTMLElement} currentItem item sélectionné dans le sous menu
 * @param {HTMLButtonElement|HTMLAnchorElement[]} parentMenus menus parents
 */
function openNextParentMenu({currentItem, parentMenus}) {
    const currentParentMenuButton = currentItem.closest('.js-quick-access__item')?.querySelector('.js-quick-access__entry');
    const currentParentMenuButtonIndex = parentMenus.findIndex((b) => b === currentParentMenuButton);
    openNextMenu({currentButtonIndex: currentParentMenuButtonIndex, menuButtons: parentMenus});
}

/**
 * Initialisation des accès rapides.
 */
document.querySelectorAll('.js-quick-access').forEach((quickAccessMenu) => {
    const quickAccessButtons = Array.from(quickAccessMenu.querySelectorAll(':scope > [role="menuitem"] .js-quick-access__entry'));
    initEventsButton(quickAccessButtons);
});
